import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../utils/api';
import { getLocalStorage, setLocalStorage, clearLocalStorage } from '../utils/storage';

const initialState = {
  isAuthenticated: !!getLocalStorage('userSession'),
  user: getLocalStorage('userSession') || null,
  tempMobile: null,
  tempEmail: null,
  tempOtp: null,
  loading: false,
  error: null,
};

export const sendOtp = createAsyncThunk(
  'auth/sendOtp',
  async (email, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/send-otp', { email });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const verifyOtp = createAsyncThunk(
  'auth/verifyOtp',
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/verify-otp', { email, otp });
      return response.data; // { message, user }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials, { rejectWithValue }) => {
    try {
      // Hardcoded Super Admin Check - STRICT ENFORCEMENT
      if (credentials.role === 'super_admin') {
        if (credentials.email === 'akkojinoor7@gmail.com' && 
            credentials.password === 'Noor@7639') {
          return {
            id: 'admin-hardcoded',
            name: 'Super Admin',
            email: 'akkojinoor7@gmail.com',
            role: 'super_admin',
            status: 'verified',
            mobile: '9999999999'
          };
        } else {
          // Reject any other super_admin login attempt
          return rejectWithValue('Invalid admin credentials');
        }
      }

      const response = await api.get('/users');
      const users = response.data;

      // BDE Login via Mobile (OTP simulation step happens before this, here we just fetch user)
      // Note: credentials.role === 'bde' logic matches here
      const user = users.find(u => u.mobile === credentials.mobile && u.role === 'bde');
      if (user) return user;
      
      return rejectWithValue('Invalid credentials');
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setTempMobile: (state, action) => {
      state.tempMobile = action.payload;
    },
    setTempEmail: (state, action) => {
      state.tempEmail = action.payload;
    },
    setTempOtp: (state, action) => {
      state.tempOtp = action.payload;
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      clearLocalStorage('userSession');
    },
    // Force login for BDE OTP flow (since we verify OTP on client for now)
    manualLogin: (state, action) => {
      state.isAuthenticated = true;
      state.user = action.payload;
      state.tempMobile = null;
      state.tempOtp = null;
      setLocalStorage('userSession', action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        setLocalStorage('userSession', action.payload);
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Send OTP
      .addCase(sendOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendOtp.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(sendOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Verify OTP
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        setLocalStorage('userSession', action.payload.user);
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setTempMobile, setTempEmail, setTempOtp, logout, manualLogin } = authSlice.actions;
export default authSlice.reducer;
